import { Injectable, Injector } from '@angular/core';
import { DeviceFingerprintService } from '../device-fingerprint.service';
import { ModalService } from '../modal.service';
import { OeAppPersonService } from '../oe-app-person.service';
import { OeAppService } from '../oe-app.service';
import { TokenService } from '../token.service';
import { MobileDeviceType } from '../types/MobileDeviceType';
import { WindowService } from '../window.service';
import { MOBILE_LOCK_MODAL_DATA } from './mobile-lock-modal.token';
import { MOBILE_LOCK_TRANSLATION_KEYS } from './mobile-lock-translations';
import { MobileLockComponent } from './mobile-lock.component';
import { MobileLockType } from './mobile-lock.types';

export const ALLOW_MOBILE_PARAM = 'oe_allow_mobile';
export const EXCLUDED_ROUTES = ['/login/angular-password-setup.html', '/token/passwordSetup.html'];

@Injectable()
export class MobileLockService {
  private readonly mobileLockType: MobileLockType;
  constructor(
    private readonly modalService: ModalService,
    private readonly windowService: WindowService,
    private readonly injector: Injector,
    private readonly oeAppPersonService: OeAppPersonService,
    private readonly oeAppService: OeAppService,
    private readonly tokenService: TokenService,
    private readonly deviceFingerprintService: DeviceFingerprintService
  ) {
    const isLoggedIn = !!oeAppPersonService.id;
    this.mobileLockType = isLoggedIn ? MobileLockType.LOGGED_IN : MobileLockType.LOGGED_OUT;
  }

  public init(): void {
    const isDesktop = !this.oeAppService.isMobileDevice();
    const isExcludedRoute = this.isExcludedRoute();
    const allowMobile = this.shouldAllowMobile();

    if (isDesktop || isExcludedRoute || allowMobile) {
      return;
    }

    this.openMobileLockModal();
  }

  private isExcludedRoute(): boolean {
    const currentPath = this.windowService.getPathName();
    return EXCLUDED_ROUTES.some((route) => currentPath.includes(route));
  }

  private shouldAllowMobile(): boolean {
    const paramAllowMobile = this.windowService.getSearchParams().get(ALLOW_MOBILE_PARAM);

    if (paramAllowMobile !== null) {
      const allowMobile = Boolean(JSON.parse(paramAllowMobile));
      sessionStorage.setItem(ALLOW_MOBILE_PARAM, String(allowMobile));
      return allowMobile;
    }

    const sessionAllowMobile = sessionStorage.getItem(ALLOW_MOBILE_PARAM);
    return Boolean(JSON.parse(sessionAllowMobile));
  }

  private openMobileLockModal(): void {
    const translationKeys = MOBILE_LOCK_TRANSLATION_KEYS[this.mobileLockType];
    const customInjector = Injector.create({
      providers: [
        {
          provide: MOBILE_LOCK_MODAL_DATA,
          useValue: {
            translationKeys: translationKeys,
            studentName: this.oeAppPersonService.firstName,
            openApp: this.openApp.bind(this),
            buildDeepLinkUrl: this.buildDeepLinkUrl.bind(this),
          },
        },
      ],
      parent: this.injector,
    });

    this.modalService.open(MobileLockComponent, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'full-screen-modal',
      injector: customInjector,
    });
  }

  public buildDeepLinkUrl(): string {
    const { webMobileDeepLinkUrl } = this.oeAppService;
    const refreshToken = this.tokenService.refreshToken;
    const lang = this.oeAppPersonService.preferredLang || navigator.language.split('-')[0];
    let url = `${webMobileDeepLinkUrl}?env=${this.oeAppService.environment}&lang=${lang}`;
    if (refreshToken) {
      url += `&oe_sso_refresh_token=${refreshToken}`;
    }
    return url;
  }

  private async openApp(): Promise<void> {
    // The app was already installed, so the app was opened and the user is logged in.
    await this.openDeepLinkUrl().catch(async (deepLinkUrl) => {
      // If the app was not installed, we need to create a SSO session and then install the app.
      this.createSSOSession(deepLinkUrl);
    });
  }

  private openDeepLinkUrl(): Promise<string> {
    const URL = this.buildDeepLinkUrl();
    this.windowService.redirectToHref(URL);
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const hasOpenedTheApp = document.visibilityState === 'hidden';
        hasOpenedTheApp ? resolve(URL) : reject(URL);
      }, 1000);
    });
  }

  private redirectToStore(deepLinkUrl: string): void {
    const storeUrl = this.buildStoreUrl(deepLinkUrl);
    if (storeUrl) this.windowService.redirectToHref(storeUrl);
  }

  private buildStoreUrl(deepLinkUrl: string): string | null {
    const androidApplicationURL = this.oeAppService.getCommonData().androidApplicationURL;
    const iosApplicationURL = this.oeAppService.getCommonData().iosApplicationURL;
    const refreshToken = this.tokenService.refreshToken;
    const mobileDeviceType = this.oeAppService.getMobileDeviceType();

    if (mobileDeviceType === MobileDeviceType.UNKNOWN) {
      return null;
    }
    if (mobileDeviceType === MobileDeviceType.ANDROID) {
      return refreshToken ? `${androidApplicationURL}&url=${encodeURIComponent(deepLinkUrl)}` : androidApplicationURL;
    }
    return iosApplicationURL;
  }

  private createSSOSession(deepLinkUrl: string): void {
    const ssoToken = this.tokenService.refreshToken || this.tokenService.token;
    this.deviceFingerprintService.createSession(ssoToken).subscribe({
      next: () => {
        this.redirectToStore(deepLinkUrl);
      },
      error: (err) => {
        console.error('Failed to create SSO session', err);
        this.redirectToStore(deepLinkUrl);
      },
    });
  }
}



// WEBPACK FOOTER //
// ./ts/app/shared/mobile-lock/mobile-lock.service.ts